/**
 * Page Init — replaces the inline <script> tags of the old PHP templates.
 *
 * Scans the DOM on DOMContentLoaded for declarative data attributes emitted
 * by the raft SSR views and executes the corresponding initialization logic.
 *
 * Supported attributes (on any element):
 *
 *   data-init-sticky-header        — sets stickyHeaderEnabled = 1
 *   data-init-sidebar-open         — opens the sidebar (#window-sidebar)
 *   data-init-load                 — calls load()
 *   data-init-focus                — focuses the element itself
 *   data-init-fn="name"            — calls the registered action `name`
 *   data-init-modal="key" [data-init-size="dynamic"] [data-init-premodal-url="/"]
 *                                  — calls modal(key, size); the premodal url is where
 *                                    the address bar returns when the modal closes
 *   data-init-redirect="url"       — sets location.href
 *   data-init-qmsg="text" [data-init-dur="2000"]  — calls qmsg(text, dur)
 *   data-init-history-replace="url" — calls history.replaceState
 *   data-init-scroll-to="elementId" [data-init-scroll-offset="90"]
 *     [data-init-scroll-container="selector"]  — scrolls to element
 *   data-init-search [data-init-autoload]   — calls searchtimed(), optionally showsearch()
 *   data-init-print                         — hides #new-navi and triggers window.print()
 *   data-init-qmsglink="text" data-init-qmsglink-modal="x" data-init-qmsglink-url="y"  — notification
 *   data-init-fade-in-delayed="selector" [data-init-delay="ms"] [data-init-dur="ms"]
 *     [data-init-unless="elementId"]  — delayed fade-in
 *   data-init-admin-search          — admin top-search focus/keyup wiring
 */

import { getAction } from "../actionRegistry";
import { setStickyHeaderEnabled, topSearchActive } from "../globalState";
import { ID_WINDOW_SIDEBAR } from "../utils/elementIds";
import { animateOpacity, queryAll } from "../utils/dom";
import { initInteriorUpload } from "./mockupUpload";
import { load } from "../ui/contentLoader";
import { modal } from "../ui/modal";
import { setPendingReturnUrl } from "../ui/ModalStack";
import { qmsg, qmsglink } from "../ui/messages";
import { showitopsearch, itopsearch } from "./search";
import { searchtimed, showsearch } from "./navigation";

export function initPageInit(): void {
  // stickyHeaderEnabled
  if (document.querySelector("[data-init-sticky-header]")) {
    setStickyHeaderEnabled(1);
  }

  // interior (mockup) PSD upload buttons — presigned S3 flow
  if (document.getElementById("uploadinterior") || document.getElementById("uploadinterior3d")) {
    initInteriorUpload();
  }

  // sidebar open
  if (document.querySelector("[data-init-sidebar-open]")) {
    const sidebar = document.getElementById(ID_WINDOW_SIDEBAR);
    if (sidebar) sidebar.style.left = "0px";
  }

  // load() call
  if (document.querySelector("[data-init-load]")) {
    load();
  }

  // focus — replaces inline `<script>$('#x').focus();</script>`
  document.querySelectorAll<HTMLElement>("[data-init-focus]").forEach((el) => {
    el.focus();
  });

  // generic function calls
  document.querySelectorAll<HTMLElement>("[data-init-fn]").forEach((el) => {
    const fnName = el.getAttribute("data-init-fn");
    if (!fnName) return;
    const fn = getAction(fnName);
    if (typeof fn !== "function") return;
    fn();
  });

  // auto-open modal
  document.querySelectorAll<HTMLElement>("[data-init-modal]").forEach((el) => {
    const key = el.getAttribute("data-init-modal");
    const size = el.getAttribute("data-init-size") ?? "dynamic";
    // Seeded BEFORE the open, which is the only moment it is read — the stack records its
    // close-target when it goes from empty to one entry. Emitted only by the modal-URL
    // route (views/routing/modalRoute.tsx), where `location.pathname` is the modal itself
    // and so cannot serve as the way back.
    const returnUrl = el.getAttribute("data-init-premodal-url");
    if (returnUrl) setPendingReturnUrl(returnUrl);
    if (key) modal(key, size);
  });

  // redirect
  const redirectEl = document.querySelector<HTMLElement>("[data-init-redirect]");
  if (redirectEl) {
    const url = redirectEl.getAttribute("data-init-redirect");
    if (url) location.href = url;
  }

  // qmsg toast
  document.querySelectorAll<HTMLElement>("[data-init-qmsg]").forEach((el) => {
    const text = el.getAttribute("data-init-qmsg");
    const dur = parseInt(el.getAttribute("data-init-dur") ?? "2000", 10);
    if (text) qmsg(text, dur);
  });

  // history.replaceState
  document.querySelectorAll<HTMLElement>("[data-init-history-replace]").forEach((el) => {
    const url = el.getAttribute("data-init-history-replace");
    if (url) history.replaceState("", "", url);
  });

  // scroll-to element
  document.querySelectorAll<HTMLElement>("[data-init-scroll-to]").forEach((el) => {
    const targetId = el.getAttribute("data-init-scroll-to");
    const offset = parseInt(el.getAttribute("data-init-scroll-offset") ?? "90", 10);
    const scrollContainer = el.getAttribute("data-init-scroll-container");
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    if (scrollContainer) {
      document.querySelector(scrollContainer)?.scrollTo({ top, behavior: "smooth" });
    } else {
      window.scrollTo({ top, behavior: "smooth" });
    }
  });

  // search trigger (searchtimed + optional showsearch)
  document.querySelectorAll<HTMLElement>("[data-init-search]").forEach((el) => {
    if (el.hasAttribute("data-init-autoload")) {
      setTimeout(() => {
        showsearch();
      }, 200);
    }
    searchtimed();
  });

  // auto-print
  if (document.querySelector("[data-init-print]")) {
    const nav = document.getElementById("new-navi");
    if (nav) nav.style.display = "none";
    window.print();
  }

  // qmsglink notifications
  document.querySelectorAll<HTMLElement>("[data-init-qmsglink]").forEach((el) => {
    const text = el.getAttribute("data-init-qmsglink");
    if (!text) return;
    const dur = parseInt(el.getAttribute("data-init-dur") ?? "10000", 10);
    const modalLink = el.getAttribute("data-init-qmsglink-modal") ?? "";
    const urlLink = el.getAttribute("data-init-qmsglink-url") ?? "";
    qmsglink(text, dur, "success", modalLink, urlLink);
  });

  // delayed fade-in (staggered)
  document.querySelectorAll<HTMLElement>("[data-init-fade-in-delayed]").forEach((el) => {
    const unless = el.getAttribute("data-init-unless");
    if (unless && document.getElementById(unless)) return;
    const selector = el.getAttribute("data-init-fade-in-delayed");
    if (!selector) return;
    const delay = parseInt(el.getAttribute("data-init-delay") ?? "0", 10);
    const dur = parseInt(el.getAttribute("data-init-dur") ?? "2000", 10);
    setTimeout(() => {
      queryAll<HTMLElement>(selector).forEach((target) => {
        animateOpacity(target, 1, dur);
      });
    }, delay);
  });

  // admin top search (nav.php)
  const adminSearch = document.querySelector<HTMLInputElement>("[data-init-admin-search]");
  if (adminSearch) {
    adminSearch.addEventListener("focus", () => {
      if (adminSearch.value.length > 2 && topSearchActive === 0) {
        showitopsearch();
      }
    });
    adminSearch.addEventListener("keyup", () => {
      itopsearch(adminSearch.value);
    });
  }
}
